import { useEffect, useRef, useState } from 'react';

const steps = [
  {
    number: '1',
    title: 'Subscribe to a Guide',
    description: 'Find a guide whose wisdom resonates with you. Subscribe to trails from that guide — they now know who you are, what you\'re working on, and how to reach you.',
    icon: (
      <svg className="w-12 h-12 text-boggler-teal" fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
      </svg>
    )
  },
  {
    number: '2',
    title: 'Guide Records for You',
    description: 'Your guide picks up their phone and creates content just for you — addressed to you by name and unlocked via your email address. Reflections on your progress, answers to your questions, personalized recommendations.',
    icon: (
      <svg className="w-12 h-12 text-orange-cta" fill="currentColor" viewBox="0 0 24 24">
        <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z"/>
      </svg>
    )
  },
  {
    number: '3',
    title: 'Watch on TV',
    description: 'Open the Boggler Trails app on your TV. Your new trail is waiting. Settle into the couch, press play, and receive personal guidance on the big screen.',
    icon: (
      <svg className="w-12 h-12 text-golden-yellow" fill="currentColor" viewBox="0 0 24 24">
        <path d="M21 3H3c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h5v2h8v-2h5c1.1 0 1.99-.9 1.99-2L23 5c0-1.1-.9-2-2-2zm0 14H3V5h18v12z"/>
      </svg>
    )
  }
];

export default function HowTrailsWork() {
  const [visibleItems, setVisibleItems] = useState(new Array(steps.length).fill(false));
  const itemRefs = useRef([]);

  useEffect(() => {
    const observers = itemRefs.current.map((ref, index) => {
      if (!ref) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry?.isIntersecting) {
            setVisibleItems(prev => {
              const newVisible = [...prev];
              newVisible[index] = true;
              return newVisible;
            });
          }
        },
        { threshold: 0.1 }
      );
      observer.observe(ref);
      return observer;
    });
    return () => {
      observers.forEach((observer) => observer?.disconnect());
    };
  }, []);

  return (
    <section className="py-20 px-6 relative z-10">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-4xl md:text-5xl text-dark-teal text-center mb-4">
          How Trails Work
        </h2>
        <p className="text-xl text-secondary-text text-center mb-16 font-lato max-w-3xl mx-auto">
          Personal video compilations of content generated by your guide, from their phone to your TV.
        </p>

        <div className="grid md:grid-cols-3 gap-12 relative">
          <div className="hidden md:block absolute top-24 left-0 right-0 h-1 bg-boggler-teal opacity-20" style={{ top: '6rem' }} />

          {steps.map((step, index) => (
            <div
              key={index}
              ref={(el) => { itemRefs.current[index] = el; }}
              className={`relative text-center scroll-animate ${visibleItems[index] ? 'visible' : ''}`}
              style={{ animationDelay: `${index * 0.2}s` }}
            >
              <div className="relative z-10 w-24 h-24 mx-auto mb-6 bg-boggler-teal rounded-full flex items-center justify-center text-white text-4xl font-lucky shadow-boggler">
                {step.number}
              </div>
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                {step.icon}
              </div>
              <h3 className="text-2xl text-heading-dark mb-3">{step.title}</h3>
              <p className="text-primary-text font-lato leading-relaxed">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
